// API Client for ARM Backend

import type {
  Account,
  CreateAccountRequest,
  UpdateAccountRequest,
  TestConnectionResponse,
  ResourcesListResponse,
  ResourcesQueryParams,
  DeleteResourceRef,
  DeleteResourceResult,
  CostSummaryParams,
  CostSummaryItem,
  AlertRule,
  CreateAlertRuleRequest,
  Alert,
  AlertsQueryParams,
} from './types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  accessToken?: string
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiError(
      (data as { error?: string }).error || `HTTP ${response.status}`,
      response.status,
      data
    );
  }

  return data as T;
}

export const api = {
  // Accounts
  accounts: {
    list: (accessToken: string) =>
      request<{ accounts: Account[] }>('/accounts', {}, accessToken),

    create: (accessToken: string, data: CreateAccountRequest) =>
      request<{ account_id: string; external_id: string }>('/accounts', {
        method: 'POST',
        body: JSON.stringify(data),
      }, accessToken),

    update: (accessToken: string, accountId: string, data: UpdateAccountRequest) =>
      request<{ account_id: string }>(`/accounts/${accountId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }, accessToken),

    delete: (accessToken: string, accountId: string) =>
      request<void>(`/accounts/${accountId}`, {
        method: 'DELETE',
      }, accessToken),

    testConnection: (accessToken: string, accountId: string) =>
      request<TestConnectionResponse>(`/accounts/${accountId}/test-connection`, {
        method: 'POST',
      }, accessToken),
  },

  // Resources
  resources: {
    list: (accessToken: string, params: ResourcesQueryParams = {}) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return request<ResourcesListResponse>(
        `/resources?${searchParams.toString()}`,
        {},
        accessToken
      );
    },

    deleteMany: (accessToken: string, items: DeleteResourceRef[]) =>
      request<{ results: DeleteResourceResult[] }>(
        '/resources/actions/delete',
        { method: 'POST', body: JSON.stringify({ items }) },
        accessToken
      ),

    costSummary: (accessToken: string, params: CostSummaryParams = {}) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return request<{ group_by: string; days: number; items: CostSummaryItem[] }>(
        `/cost/summary?${searchParams.toString()}`,
        {},
        accessToken
      );
    },
  },

  // Alerts
  alerts: {
    list: (accessToken: string, params: AlertsQueryParams = {}) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return request<{ alerts: Alert[] }>(
        `/alerts?${searchParams.toString()}`,
        {},
        accessToken
      );
    },

    acknowledge: (accessToken: string, alertId: string) =>
      request<{ alert_id: string; status: string }>(
        `/alerts/${alertId}`,
        { method: 'PATCH' },
        accessToken
      ),

    // Alert Rules
    rules: {
      list: (accessToken: string) =>
        request<{ rules: AlertRule[] }>('/alert-rules', {}, accessToken),

      create: (accessToken: string, data: CreateAlertRuleRequest) =>
        request<{ name: string }>('/alert-rules', {
          method: 'POST',
          body: JSON.stringify(data),
        }, accessToken),

      delete: (accessToken: string, ruleId: string) =>
        request<void>(`/alert-rules/${ruleId}`, {
          method: 'DELETE',
        }, accessToken),
    },
  },
};

export { ApiError };